import { CurrencyPipe } from "@angular/common";
import { AuditEntity, ISyncable } from "@app/common";
import { SyncOperation, SyncSchedule, SyncStatus, UnitPrice } from "@app/core";
import { Client, Customer, CustomerConnection } from "@app/customers-base";
import { Column, Common, Display, Table, prop, propObject, required } from "@messaia/cdk";
import { ContractStatus } from "../enums/contract-status";
import { CreditType } from "../enums/credit-type";
import { CreditReason } from "./credit-reason";
import { OrderItem } from "./order-item";

@Table({
    endpoint: 'credit',
    includes: ['Customer', 'Client', 'Reason'],
    actions: [{
        title: $localize`:@@rescind:Rescind`,
        icon: 'cancel',
        iconClass: 'tc-orange-400',
        event: (x: Credit, ctx) => ctx.addSyncSchedule(x, SyncOperation.Cancel),
        hide: (x: Credit, ctx) => !(x?.syncSchedules || []).some(x => x.operation == SyncOperation.Create && x.status == SyncStatus.Succeeded) ||
            (x?.syncSchedules || []).some(x => x.operation == SyncOperation.Cancel)
    }]
})
export class Credit extends AuditEntity implements ISyncable {
    /**
     * @property
     */
    number: number | null;

    /**
     * @property
     */
    @Column({ name: 'customer.firstName', filter: 'customer.firstName', showGtSm: true })
    @Display($localize`:@@firstName:First name`)
    private firstName: string;

    /**
     * @property
     */
    @Column({ name: 'customer.lastName', filter: 'customer.lastName' })
    @Display($localize`:@@lastName:Last name`)
    private lastName: string;

    /**
     * @property
     */
    @Column({ filterOperator: '~', name: 'customer.number', filter: 'customer.number', maxWidth: 120, showGtSm: true })
    @Display($localize`:@@customerNumber:Customer number`)
    private customerNumber: string;

    /**
     * @property
     */
    @Display($localize`:@@customer:Customer`)
    @required()
    customerId: number | null;

    /**
     * @property
     */
    customer: Customer;

    /**
     * @property
     */
    @Column({ name: 'client.name', endpoint: 'client?enabled=true&projection=id,name', maxWidth: 160, showGtXl: true })
    @Display($localize`:@@client:Client`)
    @required()
    clientId: number | null;

    /**
     * @property
     */
    client: Client;

    /**
     * @property
     */
    @Column({
        enumType: CreditType,
        width: 90,
        showGtLg: true
    })
    @required()
    @Display($localize`:@@type:Type`)
    type: CreditType;

    /**
     * @property
     */
    @prop()
    @Column({
        enumType: ContractStatus,
        cellNgClass: (row: Credit) => {
            return {
                'tc-orange-500': row.status == ContractStatus.New,
                'tc-green-500': row.status == ContractStatus.Active,
                'tc-black': row.status == ContractStatus.Cancelled,
                'tc-blue-500': row.status == ContractStatus.Terminated,
                'tc-red-500': row.status == ContractStatus.Completed
            }
        },
        width: 80,
        showGtXl: true
    })
    @Display($localize`:@@status:Status`)
    status: ContractStatus;

    /**
     * @property
     */
    @Column({
        name: 'amount.gross',
        filter: 'amount.gross',
        filterOperator: '~',
        content: (x: Credit) => new CurrencyPipe('de-DE', '€').transform(x.amount?.gross),
        maxWidth: 100,
        showGtLg: true
    })
    @Display($localize`:@@amount:Amount`)
    @required()
    @propObject(UnitPrice)
    amount: UnitPrice = new UnitPrice();

    /**
     * @property
     */
    // @Column({ width: 80, showGtLg: true })
    @prop()
    @Display($localize`:@@year:Year`)
    billYear: number;

    /**
     * @property
     */
    @Column({
        content: (row: Credit) => Common.months?.filter(x => x.id == row.billMonth)[0]?.name,
        options: Common.months,
        width: 80,
        showGtLg: true
    })
    @prop()
    @Display($localize`:@@month:Month`)
    billMonth: number;

    /**
     * @property
     */
    @required()
    @Column({
        shortDate: true,
        header: $localize`:@@startDate:Start date`,
        arrowBefore: true,
        showGtLg: true
    })
    startDate: Date;

    /**
     * @property
     */
    //@required()
    @prop()
    @Column({
        shortDate: true,
        header: $localize`:@@endDate:End date`,
        arrowBefore: true,
        showGtLg: true
    })
    endDate: Date;

    /**
     * @property
     */
    @Column({ name: 'reason.name', endpoint: 'creditreason?enabled=true&projection=id,name', maxWidth: 170, showGtLg: true })
    @Display($localize`:@@reason:Reason`)
    @required()
    reasonId: number | null;

    /**
     * @property
     */
    reason: CreditReason;

    /**
     * @property
     */
    @Display($localize`:@@contract:Contract`)
    @required()
    orderItemId: number | null;

    /**
     * @property
     */
    orderItem: OrderItem;

    /**
     * @property
     */
    @Display($localize`:@@connection:Connection`)
    @required()
    customerConnectionId: number;

    /**
     * @property
     */
    customerConnection: CustomerConnection;

    /**
     * @property
     */
    @Display($localize`:@@notice:Notice`)
    @prop()
    notice: string;

    /**
     * @property
     */
    syncSchedules: SyncSchedule[];

    /**
     * constructor
     * @param init 
     */
    public constructor(init?: Partial<Credit>) {
        super(init)
        Object.assign(this, init);

        if (!this.id) {
            /* Get current date */
            var currentDate = new Date();

            /* Substract one month */
            currentDate.setMonth(currentDate.getMonth() - 1);

            /* Set bill year and month */
            this.billYear = currentDate.getFullYear();
        }
    }
}